import { useCallback, useEffect, useRef, useState } from "react";
import { ReleaseNoteItem } from "../../interfaces";
import {
  getReleaseNotes,
  getUnseenReleaseNoteCount,
  markReleaseNoteRead,
  markReleaseNotesSeen,
} from "../../services/releaseNotes.service";

// Releases are rare: read the count when the header mounts, and again when the tab comes
// back after a while. No polling.
export const COUNT_REFRESH_AFTER_MS = 5 * 60 * 1000;
const FEED_LIMIT = 20;

export function useWhatsNew() {
  const [count, setCount] = useState(0);
  const [notes, setNotes] = useState<ReleaseNoteItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const lastCountRead = useRef(0);
  const readSent = useRef(new Set<number>());

  const refreshCount = useCallback(async () => {
    lastCountRead.current = Date.now();
    try {
      const response = await getUnseenReleaseNoteCount();
      setCount(response?.count ?? 0);
    } catch {
      // The badge is a nicety: without a count the button still opens the drawer.
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const onVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastCountRead.current > COUNT_REFRESH_AFTER_MS
      ) {
        refreshCount();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [refreshCount]);

  // Opening the drawer is what clears the badge: every note listed as new is now seen.
  const loadNotes = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const response = await getReleaseNotes(FEED_LIMIT);
      const items = Array.isArray(response) ? response : [];
      setNotes(items);
      const newIds = items
        .filter((note) => note.is_new)
        .map((note) => note.release_note_id);
      if (newIds.length) {
        setCount(0);
        try {
          const seen = await markReleaseNotesSeen(newIds);
          setCount(seen?.count ?? 0);
        } catch {
          // Not recorded: the next count read brings the badge back.
        }
      }
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const markRead = useCallback((note: ReleaseNoteItem) => {
    const id = note.release_note_id;
    if (note.read_at || readSent.current.has(id)) return;
    readSent.current.add(id);
    markReleaseNoteRead(id).catch(() => readSent.current.delete(id));
  }, []);

  return { count, notes, loading, failed, loadNotes, markRead };
}
